// Quote Builder v3 — Talmia 2.0 canonical design system.
// Two-column layout: sticky left context stack + wide right workspace.
// Product search is a keyboard-driven command palette (⌘K style).
// Autosaves on change via debounced PATCH.
//
// Wave K refactor (Feb 2026): components moved to /panels for maintainability.
// This file is now purely the orchestrator — state, autosave, and layout.
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Send, Save, ClipboardList, ChevronRight, ChevronDown, FileText, Copy, Archive, Users, MoreHorizontal,
  Banknote, Share2, CheckCircle2, Paperclip,
  Activity,
  Sparkles,
} from "lucide-react";
import { QUOTE_VARIANTS, DEFAULT_VARIANT } from "./quoteVariants";
import { unitFromCostMargin, exVatFromInc, tierUnitFromBase, marginFromUnit, effectiveCost, quoteMarginSummary, autoUrgency, URGENT_VALUE } from "./_quoteHelpers";
import Person360Overlay from "../../components/Person360Overlay";
import { resolveCrmPersonId } from "../../lib/personResolve";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import MediaManager from "../../components/MediaManager";
import { T2Panel } from "./_t2";
import { AssigneeAvatarStack } from "./_AssigneeStack";
import { OwnerChip } from "../../components/OwnerChip";
import { QuoteStatusMenu, STATUS_FLAGS } from "./quoteStatus";
import CompletionPill from "./panels/CompletionPill";
import DuplicatesButton from "./panels/DuplicatesButton";
import { useAddressBook, hasAddress } from "../../lib/addressBook";

// Panels — extracted in Wave K to keep this file focused on orchestration.
import CustomerPanel            from "./panels/CustomerPanel";
import UrgencyHeader            from "./panels/UrgencyHeader";
import CarriageButton           from "./panels/CarriageControls";
import ProductPicker            from "./panels/ProductPicker";
import LineItemsTable           from "./panels/LineItemsTable";
import DeliveryPanel            from "./panels/DeliveryPanel";
import QuoteNotesSummaryPanel, { buildQuoteSnapshot } from "./panels/QuoteNotesSummaryPanel";
import StaffNotesChat           from "./panels/StaffNotesChat";
import SummaryPanel             from "./panels/SummaryPanel";
import SendQuoteDialog          from "./panels/SendQuoteDialog";
import ActivityLogModal         from "../../components/activity/ActivityLogModal";
import LaraPanel                from "../../components/lara/LaraPanel";

/* ────────────────────────────────────────────────────────────────
   Main page — orchestration + autosave
   ──────────────────────────────────────────────────────────────── */

// Build a Customer object from a CRM person record (used by prefill + picker).
const buildCustomerFromPerson = (p) => ({
  email:     p.email,
  firstName: p.first_name || (p.full_name || "").split(" ").slice(0, -1).join(" ") || p.full_name,
  lastName:  p.last_name  || (p.full_name || "").split(" ").slice(-1).join(" ") || "",
  company:   p.company || "",
  phone:     p.phone || "",
  tier:      p.vip_tier_full || p.vip_tier || "",
  shortTier: p.vip_tier || "",
  ltv:       p.ltv ?? null,
  orders:    p.orders_count ?? null,
  avgOrder:  p.aov ?? null,
  lastOrder: p.last_order_date || null,
  personId:  p.id,
  PersonId:  p.person_id ?? null,
});

// In-memory blank draft — NEVER persisted until a customer is attached.
// `_id: null` makes scheduleSave a no-op so nothing hits the DB.
const makeDraft = (user) => ({
  _id: null,
  _draft: true,
  QuoteRef: "DRAFT",
  Status: "Draft",
  CreatedBy: user?.name || user?.email || "You",
  AccountManager: user?.name || user?.email || "",
  Customer: {},
  BuildType: "",
  Delivery: { method: "Our", band: "", cost: 0, address: "", offload: [], notes: "", includeSummaryOnPdf: true },
  Timeline: { targetDate: null, asap: false },
  LineItems: [],
  StaffNotes: [],
  CustomerNotes: "",
  CustomerFacingNotes: "",
  Totals: { sub: 0, vat: 0, total: 0 },
  Audit: [],
});

export default function QuoteBuilder({ mode = "new" }) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const prefillPersonId = searchParams.get("customer");
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [loggedThisSession, setLoggedThisSession] = useState(false);
  const [c360Id, setC360Id] = useState(null);
  const [logModal, setLogModal] = useState(null); // { kind, personId } | null
  const { user: authUser } = useAuth() || {};
  const currentUserName = useMemo(() => {
    return authUser?.name || (() => { try { return JSON.parse(localStorage.getItem("user") || "{}")?.name || "You"; } catch { return "You"; } })();
  }, [authUser]);
  const pendingRef = useRef(null);
  // Save-on-demand: pending (unsaved) changes are staged here until the user
  // presses Save. No autosave. `dirty` drives the flashing teal Save button.
  const dirtyRef = useRef({});
  const [dirty, setDirty] = useState(false);

  // Open the shared activity/note composer for this quote's customer.
  const openLog = useCallback(async (kind, draft = null) => {
    const pid = quote?.Customer?.personId || quote?.Customer?.PersonId;
    const email = quote?.Customer?.email;
    if (!pid && !email) { toast.error("Attach a customer first — nothing to log against"); return; }
    const id = await resolveCrmPersonId({ personId: pid, email });
    if (id) setLogModal({ kind, personId: id, draft });
    else toast.error("No CRM record linked to this customer yet");
  }, [quote?.Customer?.personId, quote?.Customer?.PersonId, quote?.Customer?.email]);

  // Step 2 — AI drafts an activity from the CW staff notes; the AM reviews/edits before saving.
  const [drafting, setDrafting] = useState(false);
  const draftActivity = useCallback(async () => {
    if (!quote?._id || drafting) return;
    setDrafting(true);
    try {
      const { data } = await api.post(`/quotes/${quote._id}/activity-draft`, {});
      await openLog("activity", data);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI draft failed"); }
    finally { setDrafting(false); }
  }, [quote?._id, drafting, openLog]);

  const open360 = useCallback(async () => {
    const id = await resolveCrmPersonId({ personId: quote?.Customer?.personId, email: quote?.Customer?.email });
    if (id) setC360Id(id);
    else toast.error("No CRM record linked to this customer yet");
  }, [quote?.Customer?.personId, quote?.Customer?.email]);

  // ── Tier pricing (Task Module 07) — load Trade Account Discounts once ──
  const [pricingTiers, setPricingTiers] = useState([]);
  const [pricingRules, setPricingRules] = useState({ hard_floor_pct: 3, soft_floor_pct: 6, hard_floor_blocks: false });
  // "Edit pricing rules" ability (Access → Abilities) gates the Pricing rules editor.
  const [isAdmin, setIsAdmin] = useState(false);
  const laraAdmin = true; // Lara AI is live for all users (eligibility rules still apply)
  const [laraOpen, setLaraOpen] = useState(false);
  const [laraElig, setLaraElig] = useState(null);
  const laraTotal = quote?.Totals?.total;
  useEffect(() => {
    if (!laraAdmin || !quote?._id || quote._id === "new") return;
    api.get(`/lara/quote/${quote._id}`).then(({ data }) => setLaraElig(data.eligibility)).catch(() => setLaraElig(null));
  }, [laraAdmin, quote?._id, laraTotal]);
  useEffect(() => {
    api.get("/access/me").then(({ data }) => setIsAdmin((data.capabilities || []).includes("edit_pricing_rules"))).catch(() => {});
  }, []);
  useEffect(() => {
    let alive = true;
    api.get("/config/pricing-tiers")
      .then(({ data }) => { if (alive) setPricingTiers(data.tiers || []); })
      .catch(() => {});
    api.get("/config/pricing-rules")
      .then(({ data }) => { if (alive) setPricingRules(data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Resolve a customer's VIP tier → { tier, disc, custom, label, matched }.
  // Missing/unknown tier → not matched (leave prices as-is). Data-driven from
  // the pricing_tiers config: every tier (incl. Black @ 3.5%) is a standard
  // fixed-% tier; the `custom` branch is retained only if a tier is ever
  // configured custom again. The AM can still override any line's unit £.
  const resolveTierInfo = useCallback((customer) => {
    const raw = (customer?.shortTier || customer?.tier || "").toString().trim().toLowerCase();
    const none = { tier: null, disc: 0, custom: false, label: "", matched: false };
    if (!raw || !pricingTiers.length) return none;
    const byKey = {};
    pricingTiers.forEach((t) => { byKey[String(t.tier).toLowerCase()] = t; });
    let cfg = byKey[raw] || pricingTiers.find((t) => raw.startsWith(String(t.tier).toLowerCase()));
    if (!cfg || cfg.active === false) return none;
    return {
      tier: cfg.tier,
      disc: cfg.custom ? 0 : (Number(cfg.discount_pct) || 0),
      custom: !!cfg.custom,
      label: cfg.label || "",
      matched: true,
    };
  }, [pricingTiers]);


  // Invoice (billing) address — auto-pulled from the linked Persons record.
  const [personAddr, setPersonAddr] = useState(null);
  useEffect(() => {
    const pid = quote?.Customer?.personId;
    if (!pid) { setPersonAddr(null); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/crm/persons/${pid}`);
        const p = data?.person || {};
        if (alive) setPersonAddr({
          company: p.company || "", line1: p.address1 || "", line2: p.address2 || "",
          city: p.city || "", postcode: p.post_code || "", vat: p.vat_number || "",
        });
      } catch { if (alive) setPersonAddr(null); }
    })();
    return () => { alive = false; };
  }, [quote?.Customer?.personId]);

  const addrBook = useAddressBook(quote?.Customer?.personId);
  const billing = useMemo(() => {
    const o = quote?.Customer?.billingAddress || {};
    const mode = o.mode || (hasAddress(o) ? "custom" : "saved");
    const a = (mode === "custom" || hasAddress(o)) ? o : (addrBook.data?.billing || personAddr || {});
    return {
      company:  a.company || quote?.Customer?.company || "",
      line1:    a.line1 || "",
      line2:    a.line2 || "",
      city:     a.city || "",
      county:   a.county || "",
      postcode: a.postcode || "",
      country:  a.country || "",
      vat:      o.vat || personAddr?.vat || "",
      mode,
      addressId: o.addressId || addrBook.data?.billing?.id || null,
    };
  }, [quote?.Customer, personAddr, addrBook.data]);
  const setBillingAddress = useCallback(({ mode, address, addressId }) => {
    const prev = quote?.Customer?.billingAddress || {};
    const Customer = { ...(quote?.Customer || {}), billingAddress: { ...(address || {}), vat: prev.vat || "", mode, addressId: addressId || null } };
    patchLocal({ Customer }); scheduleSave({ Customer });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.Customer]);

  // Bootstrap
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        if (mode === "edit" && routeId) {
          const { data } = await api.get(`/quotes/${routeId}`);
          if (alive) setQuote(data);
        } else if (prefillPersonId) {
          // Arrived via /quotes/new?customer=<id> — the customer is known, so
          // it's safe to persist immediately.
          try {
            const { data: pdata } = await api.get(`/crm/persons/${prefillPersonId}`);
            const p = pdata?.person;
            if (p?.email) {
              const cust = buildCustomerFromPerson(p);
              const { data } = await api.post("/quotes", { Customer: cust });
              const created = data.quote;
              if (alive) {
                setQuote(created);
                window.history.replaceState({}, "", `/quotes/${created._id}/edit`);
                toast.success(`Customer pre-attached: ${p.full_name}`);
                const existing = (pdata?.quotes || []).filter((q) => q.status !== "Archived");
                if (existing.length >= 1) {
                  toast.info(`${p.full_name} already has ${existing.length} active quote${existing.length > 1 ? "s" : ""}.`, {
                    description: "You can keep this new one or open an existing quote from View Quotes.",
                    duration: 6000,
                  });
                }
              }
            } else if (alive) {
              setQuote(makeDraft({ name: currentUserName }));
            }
          } catch (e) {
            console.warn("Prefill customer failed", e);
            if (alive) setQuote(makeDraft({ name: currentUserName }));
          }
        } else {
          // Ghost-draft fix: NO customer yet → build an in-memory draft only.
          // Nothing is written to the DB until a customer is attached.
          if (alive) setQuote(makeDraft({ name: currentUserName }));
        }
      } catch (err) {
        // Graceful handling for the 3-quote cap surfaced by POST /quotes
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 409 && detail?.code === "quote_limit_reached" && detail?.latest_quote_id) {
          toast.warning(detail.message, {
            action: {
              label: `Open ${detail.latest_quote_ref}`,
              onClick: () => navigate(`/quotes/${detail.latest_quote_id}/edit`, { replace: true }),
            },
            duration: 10000,
          });
          if (alive) navigate(`/quotes/${detail.latest_quote_id}/edit`, { replace: true });
        } else {
          toast.error("Failed to load quote");
        }
      }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [mode, routeId, prefillPersonId, navigate, currentUserName]);

  // Cmd/Ctrl+K → focus search (uses window event bubbling to child)
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        document.querySelector('[data-testid="product-search-btn"]')?.click();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const scheduleSave = useCallback((patch, { silent = false } = {}) => {
    if (!quote?._id) return;
    // Stage locally + flag unsaved. Nothing is written to the DB until the user
    // presses Save (autosave removed per request). `silent` = automatic defaults
    // (e.g. site-contact prefill) ride along with the next save without nagging.
    dirtyRef.current = { ...dirtyRef.current, ...patch };
    if (!silent) setDirty(true);
  }, [quote?._id]);

  // The ONE save path — used by the Save button and the Cmd/Ctrl+S shortcut.
  // Pushes all staged changes in a single PATCH and stamps margin+total into
  // the Audit trail (backend logs it). Self-contained (recomputes its own
  // subtotal) so it can live as a stable callback above the render guard.
  const [statusNudge, setStatusNudge] = useState(0);
  const [statusOpen, setStatusOpen] = useState(0);
  useEffect(() => {
    if (!statusNudge) return undefined;
    const t = setTimeout(() => setStatusNudge(0), 6000);
    return () => clearTimeout(t);
  }, [statusNudge]);
  const doSave = useCallback(async () => {
    if (!quote?._id || saving) return;
    setSaving(true);
    try {
      const lns = quote.LineItems || [];
      const deliv = quote.Delivery || {};
      const sub = lns.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unitPrice) || 0), 0);
      const ms = quoteMarginSummary(quote, sub, deliv);
      const patch = {
        ...dirtyRef.current,
        _SaveMargin: { pct: Number(ms.realMarginPct.toFixed(1)), total: Number(ms.grand.toFixed(2)) },
      };
      await api.patch(`/quotes/${quote._id}`, patch);
      // One summary snapshot per Save press → shown in the Audit trail tab.
      try {
        const merged = { ...quote, ...dirtyRef.current };
        await api.post(`/quotes/${quote._id}/notes`, {
          body: buildQuoteSnapshot({ quote: merged, delivery: merged.Delivery || {}, subtotal: sub, event: `Saved · margin ${patch._SaveMargin.pct}%` }),
        });
      } catch { /* snapshot is non-critical */ }
      const { data: fresh } = await api.get(`/quotes/${quote._id}`);
      setQuote(fresh);
      dirtyRef.current = {};
      setDirty(false);
      toast.success("Saved");
      setStatusNudge((n) => n + 1);
      toast(`Is "${fresh.Status || "Draft"}" still the right status?`, {
        id: "qb-status-nudge", duration: 7000, position: "bottom-right",
        description: "Keep the quote status up to date on every major save.",
        action: { label: "Update status", onClick: () => setStatusOpen((n) => n + 1) },
      });
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  }, [quote, saving]);

  // Cmd/Ctrl+S → save (power-user shortcut; mirrors the Save button exactly).
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        doSave();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [doSave]);

  // Guard against losing unsaved work now that there's no autosave.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (dirty) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  // Opening a quote clears my unread activity flags for it.
  useEffect(() => {
    if (quote?._id) api.post(`/notifications/quote/${quote._id}/seen`).catch(() => {});
  }, [quote?._id]);

  const patchLocal = (partial) => setQuote((q) => ({ ...q, ...partial }));

  // True once the quote exists in the DB (i.e. a customer has been attached).
  const persisted = !!quote?._id;
  // Nudge (pulse) the Log activity button until something is logged this session.
  const logNudge = persisted && !loggedThisSession && !!(quote?.Customer?.email || quote?.Customer?.personId);
  const duplicateQuote = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/quotes/${quote._id}/duplicate`);
      toast.success(`Duplicated as ${data.quote?.QuoteRef}`);
      navigate(`/quotes/${data.id}/edit`);
    } catch { toast.error("Duplicate failed"); }
    finally { setSaving(false); }
  };

  // Auto-urgency from target date + value (unless the AM moved the slider themselves).
  /* eslint-disable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps */
  const _autoTotal = (quote?.LineItems || []).reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unitPrice) || 0), 0) + (Number(quote?.Delivery?.cost) || 0);
  const _autoU = quote ? autoUrgency(quote, _autoTotal) : 0;
  useEffect(() => {
    if (!quote?._id) return;
    const t = quote.Timeline || {};
    if (t.urgencyManual || Number(t.desperation) === _autoU) return;
    const Timeline = { ...t, desperation: _autoU, urgencyAuto: true };
    patchLocal({ Timeline }); scheduleSave({ Timeline }, { silent: true });
  }, [quote?._id, _autoU, quote?.Timeline?.urgencyManual]);
  /* eslint-enable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps */
  const _tl = quote?.Timeline || {};
  const autoUrgencyHint = quote && (
    <div className="t2-mono" data-testid="urgency-auto-hint"
      style={{ fontSize: 10, color: "var(--t2-grey3)", margin: "-8px 0 10px 4px", display: "flex", gap: 8, alignItems: "center" }}>
      {_tl.urgencyManual ? (
        <>Urgency set manually ·
          <button type="button" data-testid="urgency-auto-reset" onClick={() => { const Timeline = { ..._tl, urgencyManual: false }; patchLocal({ Timeline }); scheduleSave({ Timeline }); }}
            style={{ background: "none", border: "none", color: "var(--t2-teal-ink)", cursor: "pointer", fontSize: 10, padding: 0 }}>back to auto ({_autoU}/10)</button></>
      ) : <>Urgency auto-set from target date{_autoTotal > URGENT_VALUE ? " + value over £3.5k" : ""} — move the slider to override</>}
    </div>
  );


  if (loading || !quote) {
    return <Layout><div style={{ textAlign: "center", color: "var(--t2-grey3)", padding: "80px 0" }}>Preparing builder…</div></Layout>;
  }

  const lines = quote.LineItems || [];
  const delivery = quote.Delivery || { method: "Our", band: "", cost: 0 };
  const timeline = quote.Timeline || { targetDate: null, asap: false, desperation: 0 };
  const subtotal = lines.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unitPrice) || 0), 0);

  const addProduct = (p) => {
    const bc = p.BCProduct || {};
    const tm = p.TMProduct || {};
    // Lead time — prefer explicit Talmia mapping, fall back to inventory heuristic
    // (in stock = 2d, out of stock = 14d) so users always see *something*.
    const leadDays = tm.LeadTimeDays ?? tm.AvailabilityDays ?? bc.availability_days ??
      ((Number(bc.inventory_level) > 0) ? 2 : 14);
    const cost = Number(bc.cost_price) || 0;
    // Live retail base = the product's REAL ex-VAT sell price (BC price is
    // inc-VAT, so ÷1.2). Tier pricing is a discount off THIS base. Fall back to
    // a cost+6% default only when the product has no sell price on record.
    const sellEx = exVatFromInc(bc.price);
    const basePrice = sellEx != null ? Number(sellEx.toFixed(4)) : unitFromCostMargin(cost, 6);
    // Auto-apply the customer's tier price on add (discount-first).
    const info = resolveTierInfo(quote.Customer);
    let unitPrice = basePrice, mode = "custom", tierKey = null, tierDisc = null;
    if (info.matched && !info.custom) {
      unitPrice = tierUnitFromBase(basePrice, info.disc);
      mode = "tier"; tierKey = info.tier; tierDisc = info.disc;
    } else if (info.matched && info.custom) {
      mode = "custom"; tierKey = info.tier;   // Black — AM sets price manually
    }
    const margin = (Number(cost) > 0) ? ((unitPrice > 0) ? Number(marginFromUnit(unitPrice, effectiveCost(cost, quote.RebatePct)).toFixed(2)) : 6) : null;
    const item = {
      productId: p._id,
      sku: bc.sku,
      name: bc.name,
      _brand: tm.BrandName || "",
      cost,
      margin,
      basePrice,
      unitPrice,
      mode,
      tierKey,
      tierDisc,
      qty: 1,
      vatRate: 0.20,
      leadTimeDays: leadDays,
      _lab_test_data: !!p._lab_test_data,
    };
    setQuote((q) => {
      const next = [...(q.LineItems || []), item];
      scheduleSave({ LineItems: next });
      return { ...q, LineItems: next };
    });
  };

  const addCustomLine = () => {
    setQuote((q) => {
      const existing = q.LineItems || [];
      // Auto-generate CUSTOM-1, CUSTOM-2 etc. based on existing custom rows
      const usedNums = existing
        .map((it) => /^CUSTOM-(\d+)$/.exec(it.sku || ""))
        .filter(Boolean)
        .map((m) => Number(m[1]));
      const nextNum = usedNums.length ? Math.max(...usedNums) + 1 : 1;
      const item = {
        productId: null,
        sku: `CUSTOM-${nextNum}`,
        name: "",
        cost: 0,
        margin: 20,
        unitPrice: 0,
        qty: 1,
        vatRate: 0.20,
        leadTimeDays: null,
        _custom: true,
      };
      const next = [...existing, item];
      scheduleSave({ LineItems: next });
      toast.success(`Added custom line ${item.sku}`);
      return { ...q, LineItems: next };
    });
  };

  // Re-apply the customer's tier price across all non-custom lines.
  const applyTierPricing = (custObj) => {
    const info = resolveTierInfo(custObj || quote.Customer);
    if (!info.matched) {
      toast.info("No VIP tier on this customer — prices left as-is.");
      return;
    }
    setQuote((q) => {
      const src = q.LineItems || [];
      let touched = 0;
      const next = src.map((it) => {
        if (it._custom) return it;               // bespoke lines untouched
        const base = Number(it.basePrice) || Number(it.unitPrice) || 0;
        if (info.custom) {                        // Black — flag for manual pricing
          touched += 1;
          return { ...it, mode: "custom", tierKey: info.tier, tierDisc: null, basePrice: base };
        }
        const unit = tierUnitFromBase(base, info.disc);
        touched += 1;
        return {
          ...it, mode: "tier", tierKey: info.tier, tierDisc: info.disc,
          basePrice: base, unitPrice: unit,
          margin: (Number(it.cost) > 0 ? Number(marginFromUnit(unit, effectiveCost(it.cost, quote.RebatePct)).toFixed(2)) : null),
        };
      });
      scheduleSave({ LineItems: next });
      if (touched) {
        info.custom
          ? toast.info(`${info.tier} is a custom tier — set each line price manually.`)
          : toast.success(`Applied ${info.tier} pricing (−${info.disc}%) to ${touched} line${touched === 1 ? "" : "s"}.`);
      }
      return { ...q, LineItems: next };
    });
  };

  // Pick a customer → attach + auto-apply their tier price to existing lines.
  // If this is an unsaved in-memory draft (_id null), CREATE the quote now —
  // this is the first moment anything is written to the DB (ghost-draft fix).
  const applyTierToLines = (items, info) => {
    if (!(info.matched && !info.custom)) return items;
    return items.map((it) => {
      if (it._custom) return it;
      const base = Number(it.basePrice) || Number(it.unitPrice) || 0;
      const unit = tierUnitFromBase(base, info.disc);
      return {
        ...it, mode: "tier", tierKey: info.tier, tierDisc: info.disc,
        basePrice: base, unitPrice: unit,
        margin: (Number(it.cost) > 0 ? Number(marginFromUnit(unit, effectiveCost(it.cost, quote.RebatePct)).toFixed(2)) : null),
      };
    });
  };

  const handlePickCustomer = async (c) => {
    const info = resolveTierInfo(c);
    // Unsaved draft → persist now (first DB write).
    if (!quote?._id) {
      const lines = applyTierToLines(quote?.LineItems || [], info);
      try {
        setSaving(true);
        const { data } = await api.post("/quotes", { Customer: c });
        let created = data.quote;
        const carry = {
          LineItems: lines,
          BuildType: quote?.BuildType || "",
          Delivery: quote?.Delivery,
          Timeline: quote?.Timeline,
          CustomerFacingNotes: quote?.CustomerFacingNotes || "",
        };
        const { data: patched } = await api.patch(`/quotes/${created._id}`, carry);
        created = patched.quote || created;
        setQuote(created);
        window.history.replaceState({}, "", `/quotes/${created._id}/edit`);
        toast.success(`Quote ${created.QuoteRef} created for ${c.firstName || c.company || c.email}`);
        if (info.matched && !info.custom && lines.length) {
          toast.success(`Applied ${info.tier} pricing (−${info.disc}%)`);
        }
      } catch (e) {
        toast.error("Could not create quote", { description: e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message });
      } finally { setSaving(false); }
      return;
    }
    // Existing quote → patch customer + re-apply tier.
    setQuote((q) => {
      const next = applyTierToLines(q.LineItems || [], info);
      scheduleSave({ Customer: c, LineItems: next });
      return { ...q, Customer: c, LineItems: next };
    });
    if (info.matched && !info.custom && (quote.LineItems || []).length) {
      toast.success(`Applied ${info.tier} pricing (−${info.disc}%)`);
    }
  };

  return (
    <Layout mainProps={{ "data-quote-builder-theme": theme }}>
      <div data-quote-builder-theme={theme} className="contents">
      {/* Identity + action bar
         Row 1 (space-between): eyebrow + CWQ + status + assignee stack   |   all action buttons right-aligned
         Row 2 (full width):    "Autosaves as you edit · Created by…"
         Row 3 (full width):    UrgencyHeader strip
      */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
            <div className="t2-page-eyebrow">
              <ClipboardList className="w-3 h-3" /> Quote builder
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <h1 style={{ fontSize: 28, fontWeight: 800, color: "var(--t2-white)", letterSpacing: "-.01em", margin: 0 }}>
                {quote.QuoteRef}
              </h1>
              {quote.ProjectRef && (
                <span data-testid="quote-header-project" title="Project name / postcode"
                  style={{ fontSize: 13, fontWeight: 700, color: "var(--t2-brass, #C17A4A)", border: "1px solid rgba(193,122,74,.4)", background: "rgba(193,122,74,.1)", borderRadius: 6, padding: "3px 9px", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {quote.ProjectRef}
                </span>
              )}
              <OwnerChip primary={quote.AccountManager} size="sm" />
              <AssigneeAvatarStack
                assignees={quote.Assignees || []}
                currentUserName={currentUserName}
                size={28}
                onChange={(next) => {
                  const patch = { Assignees: (next || []).slice(0, 5) };
                  patchLocal(patch); scheduleSave(patch);
                }}
                testidPrefix="quote-assignee"
              />
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }} data-testid="quote-actions">
            {saving && <span className="t2-mono" style={{ fontSize: 10.5, color: "var(--t2-teal-ink)" }}>Saving…</span>}
            <label data-testid="quote-variant-picker" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(232,114,44,0.12)", border: "1px solid rgba(232,114,44,0.45)", borderRadius: 10, padding: "4px 8px 4px 10px" }} title="This is the style the customer receives">
              <span className="t2-mono" style={{ fontSize: 8.5, letterSpacing: "0.12em", color: "var(--t2-coral-ink, #E8722C)", fontWeight: 700, textTransform: "uppercase" }}>📄 Template</span>
              <select
                value={quote.QuoteVariant || DEFAULT_VARIANT}
                onChange={(e) => { patchLocal({ QuoteVariant: e.target.value }); scheduleSave({ QuoteVariant: e.target.value }); }}
                title="Quote template variant — the customer-facing style"
                data-testid="quote-variant-select"
                className="qb-variant-select"
                style={{ fontSize: 12, fontWeight: 600, background: "rgba(0,0,0,0.35)", color: "var(--t2-white)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 7, padding: "5px 8px", cursor: "pointer" }}
              >
                {QUOTE_VARIANTS.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
              </select>
            </label>
            <button
              className="t2-btn-ghost qb-btn-optional"
              onClick={() => window.open(`/quotes/${quote._id}/pdf`, "_self")}
              disabled={!persisted}
              style={{ opacity: persisted ? 1 : 0.45, cursor: persisted ? "pointer" : "not-allowed" }}
              data-testid="btn-pdf-preview"
            >
              <FileText className="w-3.5 h-3.5" /> PDF preview
            </button>

            {/* Compact "⋯ More" menu — visible only ≤1100px (see qb-btn-more CSS).
                Mirrors the qb-btn-optional buttons above. */}
            <QbMoreMenu
              onPdf={() => window.open(`/quotes/${quote._id}/pdf`, "_self")}
              onDuplicate={duplicateQuote}
              saving={saving || !persisted}
            />
            <QuoteStatusMenu
              value={quote.Status || "Draft"}
              disabled={!persisted || saving}
              flash={statusNudge > 0}
              openSignal={statusOpen}
              onChange={async (next) => {
                const cur = quote.Status || "Draft";
                setStatusNudge(0);
                if (next === cur) return;
                // Archived is routed through the dedicated archive/unarchive
                // endpoints so PreArchiveStatus + list-hiding stay intact.
                if (next === "Archived") {
                  if (!window.confirm(`Archive ${quote.QuoteRef}? It will be hidden from active lists and dashboards but can be restored later.`)) return;
                  setSaving(true);
                  try { await api.post(`/quotes/${quote._id}/archive`, { reason: "Archived in Quote Builder" }); const { data } = await api.get(`/quotes/${quote._id}`); setQuote(data); toast.success("Quote archived"); }
                  catch { toast.error("Archive failed"); } finally { setSaving(false); }
                  return;
                }
                if (cur === "Archived") {
                  setSaving(true);
                  try { await api.post(`/quotes/${quote._id}/unarchive`); } catch { /* fall through to status set */ } finally { setSaving(false); }
                }
                // Keep the legacy Sent/Payment flags (SLA, dashboards) in sync.
                const f = STATUS_FLAGS[next] || { sent: false, paid: false };
                const stamp = { markedBy: quote.AccountManager || "staff", markedAt: new Date().toISOString() };
                const patch = {
                  Status: next,
                  Sent: f.sent ? { ...(quote.Sent || {}), sent: true, ...(quote.Sent?.sent ? {} : stamp) } : { sent: false, markedBy: null, markedAt: null },
                  Payment: f.paid ? { ...(quote.Payment || {}), paid: true, ...(quote.Payment?.paid ? {} : stamp) } : { paid: false, markedBy: null, markedAt: null },
                };
                // Persist IMMEDIATELY — status is a major lifecycle change and
                // must sync to View Quotes / pipelines right away, NOT staged
                // behind the manual Save (which caused it to revert). Other
                // staged edits in dirtyRef are untouched (field-scoped $set).
                patchLocal(patch);
                setSaving(true);
                try {
                  const { data } = await api.patch(`/quotes/${quote._id}`, patch);
                  if (data?.quote?.Timeline) patchLocal({ Timeline: data.quote.Timeline });
                  toast.success(`Status → ${next}`);
                  const ss = data?._stage_sync;
                  if (ss) toast.success(`${ss.pipeline === "lead" ? "Lead" : "Deal"} stage → ${ss.label}`);
                } catch {
                  patchLocal({ Status: cur, Sent: quote.Sent, Payment: quote.Payment });
                  toast.error("Status update failed");
                  setSaving(false);
                  return;
                }
                setSaving(false);
                // Log ONE snapshot per MAJOR change (a status transition only) —
                // NOT on every save/autosave. Best-effort; non-fatal on failure.
                try {
                  const merged = { ...quote, ...patch };
                  const snapshot = buildQuoteSnapshot({
                    quote: merged,
                    delivery: merged.Delivery || {},
                    subtotal: (merged.LineItems || []).reduce(
                      (s, it) => s + (Number(it.qty) || 0) * (Number(it.unitPrice) || 0), 0,
                    ),
                    transition: `${cur} → ${next}`,
                  });
                  await api.post(`/quotes/${quote._id}/notes`, { body: snapshot });
                } catch { /* snapshot note is non-critical */ }
              }}
            />
            <button className={`t2-btn-primary${logNudge ? " qb-log-nudge" : ""}`} onClick={() => openLog("activity")}
              disabled={!quote.Customer?.email && !quote.Customer?.personId}
              title="Log a call, email, follow-up or task against this customer / lead"
              style={{ background: "#7c5cff", borderColor: "#7c5cff", color: "#fff" }}
              data-testid="btn-log-activity">
              <Activity className="w-3.5 h-3.5" /> Log activity
            </button>
            {laraAdmin && (
              <button className="t2-btn-ghost" onClick={() => setLaraOpen(true)}
                disabled={!persisted || !laraElig?.eligible}
                title={laraElig?.eligible ? "Lara AI — your construction sales coach for this quote" : (laraElig?.reason || "Save the quote first")}
                style={{ borderColor: "rgba(167,139,250,0.7)", color: "#ddd6fe", background: laraElig?.eligible ? "linear-gradient(90deg, rgba(124,58,237,.28), rgba(245,158,11,.18))" : undefined }}
                data-testid="btn-lara-ai">
                <Sparkles className="w-3.5 h-3.5" /> Lara AI
              </button>
            )}
            <button className={`t2-btn-primary${dirty ? " qb-save-dirty" : ""}`} onClick={doSave}
              disabled={saving || !persisted} title={persisted ? "Save quote (⌘/Ctrl+S) — nothing is stored until you press this" : "Add a customer first — the quote saves once a customer is attached"} data-testid="btn-save">
              <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : dirty ? "Save changes" : "Save quote"}
            </button>
            <CompletionPill quote={quote} billing={billing} onPatch={(p) => { patchLocal(p); scheduleSave(p); }} />
          </div>
        </div>
        <p style={{ fontSize: 11.5, color: "var(--t2-grey3)", margin: "4px 0 8px" }} className="t2-mono">
          {persisted
            ? (dirty
                ? <span style={{ color: "var(--t2-teal-ink)", fontWeight: 700 }} data-testid="quote-unsaved-flag">● Unsaved changes — press Save to store them</span>
                : <>Saved · changes store only when you press Save · Created by <span style={{ color: "var(--t2-grey1)" }}>{quote.CreatedBy}</span></>)
            : <span style={{ color: "var(--t2-brass-ink)" }}>Draft not saved yet — add a customer to create this quote. Nothing is stored until then.</span>}
        </p>
        <UrgencyHeader
          quote={quote}
          onChange={(patch) => { patchLocal(patch); scheduleSave(patch); }}
        />
        {autoUrgencyHint}
      </div>

      {/* Two-column grid: ~340px sticky context + fluid workspace.
         Collapses to a single column ≤768px via .qb-shell-grid CSS. */}
      <div className="qb-shell-grid" style={{ display: "grid", gridTemplateColumns: "minmax(320px, 340px) 1fr", gap: 20 }}>
        {/* LEFT — sticky context stack */}
        <aside style={{ position: "sticky", top: 84, alignSelf: "start", maxHeight: "calc(100vh - 100px)", overflowY: "auto", paddingRight: 4 }} className="t2-scroll qb-shell-aside">
          <CustomerPanel
            customer={quote.Customer}
            billing={billing}
            addrBook={addrBook}
            onBillingChange={setBillingAddress}
            delivery={delivery}
            onDeliveryAddrChange={({ mode, address, addressId }) => {
              const next = { ...delivery, addressSource: mode === "custom" ? "manual" : mode, addressId: addressId || null, ...(address ? { deliveryAddress: address } : {}) };
              patchLocal({ Delivery: next }); scheduleSave({ Delivery: next });
            }}
            tierInfo={resolveTierInfo(quote.Customer)}
            onPick={handlePickCustomer}
            onClear={() => { patchLocal({ Customer: {} }); scheduleSave({ Customer: {} }); }}
            onLog={openLog}
            onOpen360={open360}
          />
          <SummaryPanel quote={quote} subtotal={subtotal} delivery={delivery} />
          <T2Panel icon={Copy} title="Quote tools" testid="quote-tools-panel">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <button className="t2-btn-ghost t2-btn-full" onClick={duplicateQuote} disabled={saving || !persisted}
                style={{ fontSize: 10.5, opacity: persisted ? 1 : 0.45 }} data-testid="btn-duplicate" title="Make a copy of this quote">
                <Copy className="w-3.5 h-3.5" /> Duplicate
              </button>
              {persisted
                ? <DuplicatesButton quoteId={quote._id} customerEmail={quote.Customer?.email} full />
                : <button className="t2-btn-ghost t2-btn-full" disabled style={{ fontSize: 10.5, opacity: 0.45 }}>Duplicates</button>}
            </div>
          </T2Panel>
          <T2Panel icon={Paperclip} title="Attachments" testid="quote-attachments-panel">
            {persisted
              ? <MediaManager contextType="quote" entityId={quote._id} compact />
              : <div style={{ fontSize: 11, color: "var(--t2-grey3)" }}>Attach a customer first to upload files.</div>}
          </T2Panel>
          {/* QuoteDetailsPanel removed (Batch 1 · Feb 2026):
             Ref/Status live in the top header, Build/Expiry in UrgencyHeader,
             Assignee on the AssigneeAvatarStack next to the h1 — this panel
             was fully redundant. */}
        </aside>

        {/* RIGHT — wide workspace */}
        <div>
          <ProductPicker onAdd={addProduct} />

          <LineItemsTable
            items={lines}
            supplier={quote.Supplier}
            rebatePct={quote.RebatePct}
            pricingTier={resolveTierInfo(quote.Customer)}
            onApplyTier={applyTierPricing}
            rules={pricingRules}
            isAdmin={isAdmin}
            onRulesChange={setPricingRules}
            onChange={(next) => { patchLocal({ LineItems: next }); scheduleSave({ LineItems: next }); }}
            onAddCustom={addCustomLine}
            carriageCost={delivery?.cost}
            headerExtra={<CarriageButton delivery={delivery} subtotal={subtotal}
              onChange={(next) => { patchLocal({ Delivery: next }); scheduleSave({ Delivery: next }); }} />}
          />

          {/* Site & delivery — internal 3×1 grid (Delivery address / Site&Access incl. Build /
              On-site facts + merged Customer/To-customer notes). Carriage lives in a pop-up. */}
          <DeliveryPanel
            quote={quote} delivery={delivery} subtotal={subtotal} billing={billing} addrBook={addrBook}
            onBuildChange={(b) => { patchLocal({ BuildType: b }); scheduleSave({ BuildType: b }); }}
            onProjectChange={(v) => { patchLocal({ ProjectRef: v }); scheduleSave({ ProjectRef: v }); }}
            customerFacingNotes={quote.CustomerFacingNotes}
            onChange={(next) => { patchLocal({ Delivery: next }); scheduleSave({ Delivery: next }); }}
            onPrefill={(next) => { patchLocal({ Delivery: next }); scheduleSave({ Delivery: next }, { silent: true }); }}
            onNotesChange={(text) => {
              const nextDelivery = { ...delivery, notes: text };
              patchLocal({ Delivery: nextDelivery, CustomerFacingNotes: text });
              scheduleSave({ Delivery: nextDelivery, CustomerFacingNotes: text });
            }}
          >
            <QuoteNotesSummaryPanel
              quote={quote}
              delivery={delivery}
              subtotal={subtotal}
              billing={billing}
              onChange={(next) => { patchLocal({ Delivery: next }); scheduleSave({ Delivery: next }); }}
            />
          </DeliveryPanel>

          {/* Bottom: CW Staff Notes (full width) */}
          <div data-testid="quote-bottom-3col">
            <StaffNotesChat
              quoteId={quote._id}
              buildType={quote.BuildType || ""}
              notes={quote.StaffNotes || []}
              audit={quote.Audit || []}
              currentUserName={currentUserName}
              canEditAny={["admin", "manager"].includes(authUser?.role)}
              onLog={openLog}
              onDraftActivity={draftActivity}
              onSend={async (body, mentions) => {
                try {
                  await api.post(`/quotes/${quote._id}/notes`, { body, mentions });
                  const { data } = await api.get(`/quotes/${quote._id}`);
                  setQuote(data);
                } catch { toast.error("Failed to add note"); }
              }}
              onEdited={async () => { const { data } = await api.get(`/quotes/${quote._id}`); setQuote(data); }}
            />
          </div>
        </div>
      </div>

      <Person360Overlay personId={c360Id} onClose={() => setC360Id(null)} />
      {laraOpen && <LaraPanel quoteId={quote._id} quoteRef={quote.QuoteRef} onClose={() => setLaraOpen(false)} />}

      {logModal && (
        <ActivityLogModal
          kind={logModal.kind}
          personId={logModal.personId}
          defaultAssigneeName={typeof quote.AccountManager === "string" ? quote.AccountManager : quote.AccountManager?.name}
          contextLabel={[quote.Customer?.firstName, quote.Customer?.lastName].filter(Boolean).join(" ") || quote.Customer?.email}
          quoteId={quote._id}
          quoteRef={quote.QuoteRef}
          draft={logModal.draft}
          onClose={() => setLogModal(null)}
          onSaved={async () => {
            setLogModal(null);
            setLoggedThisSession(true);
            try { const { data } = await api.get(`/quotes/${quote._id}`); setQuote((cur) => ({ ...cur, StaffNotes: data.StaffNotes, Audit: data.Audit })); } catch { /* noop */ }
          }}
        />
      )}

      <SendQuoteDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        quote={quote}
        onSent={async () => {
          try {
            const { data } = await api.get(`/quotes/${quote._id}`);
            setQuote(data);
          } catch (err) {
            if (process.env.NODE_ENV !== "production") console.warn("Post-send quote refresh failed:", err);
          }
        }}
      />
      </div>
    </Layout>
  );
}


/* ────────────────────────────────────────────────────────────────
   Compact "⋯ More" menu (Batch 2 · Feb 2026)
   Visible only ≤1100px via .qb-btn-more CSS.
   Mirrors the 4 collapsible header actions (Overview / PDF / Duplicate / Archive)
   so the always-visible cluster stays: 360° · Send · Save.
   ──────────────────────────────────────────────────────────────── */
function QbMoreMenu({ onPdf, onDuplicate, saving }) {
  const [open, setOpen] = useState(false);
  const rowStyle = {
    display: "flex", alignItems: "center", gap: 8,
    padding: "8px 12px", width: "100%", cursor: "pointer",
    background: "transparent", border: "none", color: "var(--t2-grey1)",
    fontSize: 12, textAlign: "left",
  };
  return (
    <div className="qb-btn-more" style={{ position: "relative", display: "none" }}>
      <button
        className="t2-btn-ghost"
        onClick={() => setOpen((o) => !o)}
        data-testid="btn-more-menu"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal className="w-3.5 h-3.5" /> More
      </button>
      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 40 }}
            data-testid="btn-more-backdrop"
          />
          <div
            role="menu"
            data-testid="btn-more-dropdown"
            style={{
              position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 41,
              minWidth: 180, background: "var(--t2-ink)",
              border: "1px solid var(--t2-border)", borderRadius: 8,
              boxShadow: "0 12px 32px rgba(0,0,0,0.45)", padding: 4,
            }}
          >
            <button style={rowStyle} onClick={() => { setOpen(false); onPdf(); }} data-testid="more-item-pdf">
              <FileText className="w-3.5 h-3.5" /> PDF preview
            </button>
            <button style={rowStyle} onClick={() => { setOpen(false); onDuplicate(); }} disabled={saving} data-testid="more-item-duplicate">
              <Copy className="w-3.5 h-3.5" /> Duplicate
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* Quote status pill + dropdown, flags and colours now live in ./quoteStatus. */
